import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Search, ChevronDown, Menu, X } from 'lucide-react';
import {
  getAllTools,
  getCategories,
  getPopularTools,
  SITE,
} from '../../lib/toolRegistry';
import { Logo, Wordmark } from '../brand/Logo';
import { cn } from '@devkit/ui';
import { useUI } from '../../store/ui';

interface HeaderProps {
  onOpenSearch?: () => void;
}

export default function Header(_: HeaderProps = {}) {
  const setPaletteOpen = useUI((s) => s.setPaletteOpen);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const toolsMenuRef = useRef<HTMLDivElement>(null);

  // Close menus on route change
  useEffect(() => {
    setToolsOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  // Click-outside to close Tools dropdown
  useEffect(() => {
    if (!toolsOpen) return;
    const handler = (e: MouseEvent) => {
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(e.target as Node)) {
        setToolsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [toolsOpen]);

  const allTools = getAllTools();
  const popular = getPopularTools();
  const categories = getCategories();
  const toolsByCategory = categories.map((cat) => ({
    category: cat,
    tools: allTools.filter((t) => t.category === cat.id),
  })).filter((g) => g.tools.length > 0);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-800/50 bg-background/80 backdrop-blur-md">
      <nav className="container mx-auto px-4 h-16 flex items-center justify-between gap-4" aria-label="Primary">
        {/* Brand */}
        <Link
          to="/"
          className="flex items-center gap-2 group flex-shrink-0"
          aria-label={`${SITE.name} home`}
        >
          <Logo size={32} className="group-hover:scale-105 transition-transform" />
          <Wordmark />
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-1">
          {/* Tools dropdown */}
          <div ref={toolsMenuRef} className="relative">
            <button
              type="button"
              aria-haspopup="true"
              aria-expanded={toolsOpen}
              onClick={() => setToolsOpen((v) => !v)}
              className={cn(
                'flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                toolsOpen
                  ? 'text-white bg-slate-800/60'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/40'
              )}
            >
              Tools
              <ChevronDown
                className={cn('w-4 h-4 transition-transform', toolsOpen && 'rotate-180')}
                aria-hidden
              />
            </button>
            {toolsOpen && (
              <div
                role="menu"
                className="absolute left-0 mt-2 w-[640px] bg-surface border border-slate-700 rounded-xl shadow-2xl shadow-black/40 p-4 grid grid-cols-2 gap-x-6 gap-y-4"
              >
                {/* Popular */}
                <div className="col-span-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 px-2">
                    Popular
                  </p>
                  <div className="grid grid-cols-2 gap-1">
                    {popular.slice(0, 4).map((t) => (
                      <Link
                        key={t.slug}
                        to={`/${t.slug}`}
                        className="flex items-start gap-2 p-2 rounded-md hover:bg-slate-700/50 transition-colors"
                      >
                        <span className="text-sm text-white">{t.name}</span>
                        <span className="text-xs text-slate-500 line-clamp-1">
                          {t.shortDescription}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
                <div className="col-span-2 h-px bg-slate-800" />
                {/* Categories */}
                {toolsByCategory.map(({ category, tools }) => (
                  <div key={category.id} className="col-span-1">
                    <Link
                      to={`/tools/${category.id}`}
                      className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 px-2 hover:text-slate-300 transition-colors"
                    >
                      {category.name}
                    </Link>
                    <ul className="space-y-0.5">
                      {tools.slice(0, 4).map((t) => (
                        <li key={t.slug}>
                          <Link
                            to={`/${t.slug}`}
                            className="block px-2 py-1 rounded text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors"
                          >
                            {t.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <div className="col-span-2 pt-2 mt-2 border-t border-slate-800">
                  <Link
                    to="/tools"
                    className="text-sm text-primary hover:underline"
                  >
                    Browse all tools →
                  </Link>
                </div>
              </div>
            )}
          </div>

          <NavLink
            to="/guides"
            className={({ isActive }) =>
              cn(
                'px-3 py-2 rounded-md text-sm font-medium transition-colors',
                isActive
                  ? 'text-white bg-slate-800/60'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/40'
              )
            }
          >
            Guides
          </NavLink>
        </div>

        {/* Right: Search + mobile menu */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700 text-slate-400 hover:text-white text-sm transition-colors"
            aria-label="Open search"
          >
            <Search className="w-4 h-4" aria-hidden />
            <span className="hidden sm:inline">Search</span>
            <kbd className="hidden sm:inline-block ml-2 text-[10px] font-mono px-1 py-0.5 rounded bg-slate-900 text-slate-500 border border-slate-700">
              ⌘K
            </kbd>
          </button>

          <button
            type="button"
            className="md:hidden p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/40"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle navigation"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-slate-800 bg-background/95 backdrop-blur">
          <div className="container mx-auto px-4 py-4 space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                Tools
              </p>
              <div className="grid grid-cols-2 gap-1">
                {allTools.slice(0, 12).map((t) => (
                  <Link
                    key={t.slug}
                    to={`/${t.slug}`}
                    className="px-3 py-2 rounded text-sm text-slate-200 hover:bg-slate-800/60"
                  >
                    {t.name}
                  </Link>
                ))}
              </div>
              <Link
                to="/tools"
                className="block mt-2 px-3 py-2 text-sm text-primary"
              >
                Browse all tools →
              </Link>
            </div>
            <Link
              to="/guides"
              className="block px-3 py-2 rounded text-sm text-slate-200 hover:bg-slate-800/60"
            >
              Guides
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
